import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { restaurantOrderService } from '../../../../services/restaurantOrderService';
import { RestaurantOrderDTO, IssueKotResponseDTO } from '../../../../types';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { ArrowLeft, Loader2, ChefHat, Martini, CreditCard, Ban } from 'lucide-react';
import '../RestaurantPage.css';

const NEXT_STATUSES: Record<string, string> = {
  Open: 'Sent', Sent: 'InPreparation', InPreparation: 'Ready', Ready: 'Completed',
};

const RestaurantOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();
  const [order, setOrder] = useState<RestaurantOrderDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState<string | null>(null);

  useEffect(() => { loadOrder(); }, [id]); // eslint-disable-line

  const loadOrder = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await restaurantOrderService.getById(parseInt(id));
      const d = (res.data as any)?.data ?? res.data;
      setOrder(d);
    } catch { setError(t('restaurant.orderDetail.failedToLoad')); }
    finally { setLoading(false); }
  };

  const advanceStatus = async () => {
    if (!order || !NEXT_STATUSES[order.status]) return;
    setUpdating(true);
    try {
      await restaurantOrderService.updateStatus(order.id, NEXT_STATUSES[order.status]);
      await loadOrder();
    } catch { setError(t('restaurant.orderDetail.failedToUpdateStatus')); }
    finally { setUpdating(false); }
  };

  /* ── Void (cancel) flow ── */
  // Prints a "VOID" cancellation ticket per KOT/BOT type already sent, so the
  // kitchen/bar knows to stop preparing the listed items.
  const printVoidTicket = (ticket: IssueKotResponseDTO, reason: string) => {
    const rows = ticket.lines.map(l => `
      <tr>
        <td style="padding:5px 8px;font-weight:700;font-size:16px;width:36px;">${l.quantity}×</td>
        <td style="padding:5px 8px;font-size:15px;text-decoration:line-through;">${l.itemName}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><title>VOID ${ticket.ticketType}</title>
<style>body{font-family:'Courier New',monospace;margin:0;padding:12px;max-width:300px;}
.hdr{text-align:center;border-bottom:2px dashed #000;padding-bottom:8px;margin-bottom:8px;}
.badge{display:inline-block;background:#000;color:#fff;padding:3px 14px;font-size:20px;font-weight:900;letter-spacing:3px;}
.num{font-size:14px;font-weight:700;margin-top:4px;}
table{width:100%;border-collapse:collapse;}
.reason{border-top:2px dashed #000;margin-top:8px;padding-top:6px;font-size:12px;font-weight:700;}
.ftr{text-align:center;border-top:2px dashed #000;margin-top:8px;padding-top:6px;font-size:11px;}
@media print{body{margin:0;}}</style></head><body>
<div class="hdr">
  <div class="badge">✕ VOID ${ticket.ticketType}</div>
  <div class="num">${ticket.ticketNumber}</div>
  <div style="margin-top:4px;font-size:12px;">${ticket.tableNumber ? 'Table: ' + ticket.tableNumber : ticket.orderType}</div>
  <div style="font-size:11px;">${new Date(ticket.printedAt).toLocaleString()}</div>
  <div style="margin-top:4px;font-size:13px;font-weight:900;">DO NOT PREPARE</div>
</div>
<table>${rows}</table>
<div class="reason">Reason: ${reason}</div>
<div class="ftr">— ${ticket.lines.length} item(s) cancelled —</div>
</body></html>`;

    const win = window.open('', '_blank', 'width=320,height=500,menubar=no,toolbar=no');
    if (!win) { alert('Allow popups to print the void ticket.'); return; }
    win.document.write(html);
    win.document.close();
    setTimeout(() => { try { win.print(); } catch { /* ignore */ } }, 350);
  };

  const confirmVoid = async () => {
    if (!order) return;
    const reason = voidReason.trim();
    if (!reason) { setVoidError(t('restaurant.orderDetail.voidModal.reasonRequired')); return; }
    setUpdating(true);
    setVoidError(null);
    try {
      const res = await restaurantOrderService.voidOrder(order.id, reason);
      const data = (res.data as any)?.data ?? res.data;
      (data?.voidTickets ?? []).forEach((ticket: IssueKotResponseDTO) => printVoidTicket(ticket, reason));
      setShowVoidModal(false);
      setVoidReason('');
      await loadOrder();
    } catch (err: unknown) {
      const axErr = err as import('axios').AxiosError<{ error?: { message?: string } }>;
      setVoidError(axErr?.response?.data?.error?.message || t('restaurant.orderDetail.failedToVoid'));
    } finally { setUpdating(false); }
  };

  const isSettled = order?.status === 'Completed';
  const tickets = order?.kotTickets ?? [];

  if (loading) return <Layout><div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Loader2 size={24} /></div></Layout>;

  return (
    <Layout>
      <div className="ims-detail-page rest-detail-page">
        {/* Slim blue banner */}
        <div className="rest-order-banner">
          <button className="rest-order-banner-back" onClick={() => navigate('/restaurant/orders')}>
            <ArrowLeft size={15} />
          </button>
          <div className="rest-order-banner-info">
            <span className="rest-order-banner-number">{order?.orderNumber}</span>
            <span className="rest-order-banner-sub">
              {order?.outletName || 'No outlet'} · {order?.orderType}
              {order?.tableNumber ? ` · ${order.tableNumber}` : ''}
            </span>
          </div>
          <span className={`rest-order-banner-status rest-status-dot-${order?.status ?? ''}`}>
            ● {order?.status ? t(`restaurant.orderDetail.statusLabels.${order.status}`, order.status) : ''}
          </span>
          <div className="rest-order-banner-actions">
            {NEXT_STATUSES[order?.status ?? ''] && (
              <button className="ims-btn ims-btn-primary" disabled={updating} onClick={advanceStatus}>
                {updating ? t('restaurant.orderDetail.updating') : `→ ${t(`restaurant.orderDetail.statusLabels.${NEXT_STATUSES[order?.status ?? '']}`, NEXT_STATUSES[order?.status ?? ''])}`}
              </button>
            )}
            {order?.status && !['Completed', 'Voided'].includes(order.status) && (
              <button className="btn-delete" disabled={updating} onClick={() => { setVoidError(null); setShowVoidModal(true); }}>{t('restaurant.orderDetail.voidModal.confirm', 'Void')}</button>
            )}
          </div>
        </div>

        {error && <div className="ims-alert ims-alert-error" style={{ margin: '1rem 0' }}>{error}<button className="btn-close" onClick={() => setError(null)} /></div>}

        {order && (
          <div className="ims-detail-body">
            <div className="ims-detail-main">
              <div className="ims-card">
                <div className="ims-card-header"><span className="ims-card-title">{t('restaurant.orderDetail.orderLines')}</span></div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="rest-data-table">
                    <thead>
                      <tr>
                        {[
                          t('restaurant.orderDetail.columns.item'),
                          t('restaurant.orderDetail.columns.unitPrice'),
                          t('restaurant.orderDetail.columns.qty'),
                          t('restaurant.orderDetail.columns.lineTotal'),
                          t('restaurant.orderDetail.columns.notes'),
                        ].map(h => <th key={h}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {order.lines.map(l => (
                        <tr key={l.id}>
                          <td style={{ fontWeight: 500 }}>{l.itemName}</td>
                          <td>{fmt(Number(l.unitPrice))}</td>
                          <td style={{ fontWeight: 600 }}>{l.quantity}</td>
                          <td style={{ fontWeight: 600 }}>{fmt(Number(l.lineTotal))}</td>
                          <td style={{ color: 'var(--ims-text-sub)' }}>{l.modifierNotes || l.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* KOT / BOT tickets issued for this order */}
              <div className="ims-card" style={{ marginTop: '1rem' }}>
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <ChefHat size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('restaurant.orderDetail.kotBotTickets')}
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: tickets.length === 0 ? undefined : 0 }}>
                  {tickets.length === 0 ? (
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--ims-text-sub)' }}>
                      {t('restaurant.orderDetail.noTickets')}
                    </p>
                  ) : (
                    <div className="rest-ticket-list">
                      {tickets.map(t => (
                        <div key={t.id} className="rest-ticket">
                          <div className="rest-ticket-head">
                            <span className={`rest-ticket-type ${t.isVoid ? 'rest-ticket-type-void' : t.ticketType === 'BOT' ? 'rest-ticket-type-bot' : ''}`}>
                              {t.isVoid
                                ? <Ban size={12} style={{ verticalAlign: 'middle', marginInlineEnd: 4 }} />
                                : t.ticketType === 'BOT'
                                  ? <Martini size={12} style={{ verticalAlign: 'middle', marginInlineEnd: 4 }} />
                                  : <ChefHat size={12} style={{ verticalAlign: 'middle', marginInlineEnd: 4 }} />}
                              {t.isVoid ? `VOID ${t.ticketType}` : t.ticketType}
                            </span>
                            <span className="rest-ticket-number">{t.ticketNumber}</span>
                            <span className="rest-ticket-time">{new Date(t.printedAt).toLocaleString()}</span>
                          </div>
                          {t.lines.length > 0 && (
                            <ul className="rest-ticket-lines">
                              {t.lines.map((l, i) => (
                                <li key={i}>
                                  <span className="rest-ticket-qty">{l.quantity}×</span> {l.itemName}
                                  {(l.modifierNotes || l.notes) && (
                                    <span className="rest-ticket-note"> — {l.modifierNotes || l.notes}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="ims-detail-sidebar">
              {/* Cancellation details — voided orders only */}
              {order.status === 'Voided' && (
                <div className="ims-card" style={{ marginBottom: '1rem' }}>
                  <div className="ims-card-header">
                    <span className="ims-card-title">
                      <Ban size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                      {t('restaurant.orderDetail.voided')}
                    </span>
                    <span className="ims-badge ims-badge-rejected">{t('restaurant.orderDetail.cancelled')}</span>
                  </div>
                  <div className="ims-card-body">
                    <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div>
                        <span style={{ color: 'var(--ims-text-sub)' }}>{t('restaurant.orderDetail.reason')}: </span>
                        <span style={{ fontWeight: 600 }}>{order.voidReason || '—'}</span>
                      </div>
                      {order.modifiedAt && (
                        <div style={{ fontSize: '11px', color: 'var(--ims-text-sub)' }}>
                          {t('restaurant.orderDetail.voidedAt', { date: new Date(order.modifiedAt).toLocaleString() })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="ims-card">
                <div className="ims-card-header"><span className="ims-card-title">{t('restaurant.orderDetail.orderSummary')}</span></div>
                <div className="ims-card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                    {[
                      [t('restaurant.orderDetail.table'), order.tableNumber || '—'],
                      [t('restaurant.orderDetail.customer'), order.customerName || '—'],
                      [t('restaurant.orderDetail.subTotal'), fmt(Number(order.subTotal))],
                      [t('restaurant.orderDetail.tax'), fmt(Number(order.taxAmount))],
                      [t('restaurant.orderDetail.serviceCharge'), fmt(Number(order.serviceChargeAmount))],
                      ...(order.deliveryChargeAmount > 0 ? [[t('restaurant.orderDetail.deliveryCharge'), fmt(Number(order.deliveryChargeAmount))]] : []),
                      ...(order.discountAmount > 0 ? [[t('restaurant.orderDetail.discount'), `-${fmt(Number(order.discountAmount))}`]] : []),
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--ims-text-sub)' }}>{k}</span><span>{v}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '15px', paddingTop: '8px', borderTop: '1px solid var(--ims-border)' }}>
                      <span>{t('restaurant.orderDetail.total')}</span><span>{fmt(Number(order.totalAmount))}</span>
                    </div>
                  </div>
                  {order.notes && <p style={{ marginTop: '1rem', fontSize: '12px', color: 'var(--ims-text-sub)' }}>{order.notes}</p>}
                  <div style={{ marginTop: '1rem', fontSize: '11px', color: 'var(--ims-text-sub)' }}>
                    {new Date(order.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Payment details — settled orders only */}
              {isSettled && (
                <div className="ims-card" style={{ marginTop: '1rem' }}>
                  <div className="ims-card-header">
                    <span className="ims-card-title">
                      <CreditCard size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                      {t('restaurant.orderDetail.payment')}
                    </span>
                    <span className="ims-badge ims-badge-approved">{t('restaurant.orderDetail.paid')}</span>
                  </div>
                  <div className="ims-card-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--ims-text-sub)' }}>{t('restaurant.orderDetail.method')}</span>
                        <span style={{ fontWeight: 600 }}>{order.paymentMethod || '—'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--ims-text-sub)' }}>{t('restaurant.orderDetail.amountPaid')}</span>
                        <span>{fmt(Number(order.amountPaid))}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--ims-text-sub)' }}>{t('restaurant.orderDetail.changeGiven')}</span>
                        <span>{fmt(Number(order.changeGiven))}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, paddingTop: '8px', borderTop: '1px solid var(--ims-border)' }}>
                        <span>{t('restaurant.orderDetail.totalSettled')}</span><span>{fmt(Number(order.totalAmount))}</span>
                      </div>
                      {order.modifiedAt && (
                        <div style={{ fontSize: '11px', color: 'var(--ims-text-sub)' }}>
                          {t('restaurant.orderDetail.settledAt', { date: new Date(order.modifiedAt).toLocaleString() })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Void reason modal */}
        {showVoidModal && (
          <div className="rest-void-overlay" onClick={() => !updating && setShowVoidModal(false)}>
            <div className="rest-void-modal" onClick={e => e.stopPropagation()}>
              <h3 className="rest-void-title">
                <Ban size={17} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                {t('restaurant.orderDetail.voidModal.title', { number: order?.orderNumber })}
              </h3>
              <p className="rest-void-hint">
                {t('restaurant.orderDetail.voidModal.hint')}
              </p>
              <label className="rest-void-label" htmlFor="voidReason">{t('restaurant.orderDetail.voidModal.reasonLabel')}</label>
              <textarea
                id="voidReason"
                className="rest-void-textarea"
                rows={3}
                maxLength={500}
                placeholder={t('restaurant.orderDetail.voidModal.placeholder')}
                value={voidReason}
                onChange={e => { setVoidReason(e.target.value); if (voidError) setVoidError(null); }}
                autoFocus
              />
              {voidError && <div className="ims-alert ims-alert-error" style={{ marginTop: '8px' }}>{voidError}</div>}
              <div className="rest-void-actions">
                <button className="ims-btn ims-btn-secondary" disabled={updating} onClick={() => setShowVoidModal(false)}>
                  {t('restaurant.orderDetail.voidModal.keep')}
                </button>
                <button className="btn-delete" disabled={updating || !voidReason.trim()} onClick={confirmVoid}>
                  {updating ? t('restaurant.orderDetail.voidModal.voiding') : t('restaurant.orderDetail.voidModal.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RestaurantOrderDetailPage;
